import { useCallback, useEffect, useState } from 'react';
import { Users, Building2, ShieldAlert } from 'lucide-react';
import { useAuth } from '../../state/AuthContext';
import { adminApi, ApiError } from '../../lib/api';
import type { Organization, User, UserRole } from '../../lib/types';
import { Spinner, ErrorBanner, EmptyState } from '../../components/Misc';
import { formatDateTime, titleCase } from '../../lib/format';

const ROLES: UserRole[] = ['platform_admin', 'org_admin', 'safety_officer', 'area_authority', 'performing_authority', 'viewer'];
const PLAN_TIERS = ['trial', 'starter', 'professional', 'enterprise'] as const;

export default function AdminPage() {
  const { user: me } = useAuth();
  const isPlatformAdmin = me?.role === 'platform_admin';

  const [users, setUsers] = useState<Array<User & { orgName?: string | null }> | null>(null);
  const [orgs, setOrgs] = useState<Organization[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const requests: Promise<unknown>[] = [adminApi.users().then((r) => setUsers(r.users))];
      if (isPlatformAdmin) requests.push(adminApi.orgs().then((r) => setOrgs(r.orgs)));
      await Promise.all(requests);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load admin data.');
    }
  }, [isPlatformAdmin]);

  useEffect(() => {
    void load();
  }, [load]);

  async function changeRole(userId: string, role: UserRole) {
    setBusy(`role-${userId}`);
    setError(null);
    try {
      await adminApi.setUserRole(userId, role);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change role.');
    } finally {
      setBusy(null);
    }
  }

  async function toggleStatus(userId: string, status: 'active' | 'suspended') {
    setBusy(`status-${userId}`);
    setError(null);
    try {
      await adminApi.setUserStatus(userId, status);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change status.');
    } finally {
      setBusy(null);
    }
  }

  async function updatePlan(orgId: string, input: { planTier: string; maxActivePermits: number; maxPlants: number }) {
    setBusy(`plan-${orgId}`);
    setError(null);
    try {
      await adminApi.setPlan(orgId, input);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not update plan.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="stack gap-5">
      <div>
        <h1 className="h2-section">Admin</h1>
        <p className="body-text text-secondary">
          {isPlatformAdmin ? 'Manage organizations, plans, and users across the entire platform.' : 'Manage users within your organization.'}
        </p>
      </div>

      {error && <ErrorBanner message={error} />}

      {isPlatformAdmin && (
        <section className="stack gap-3">
          <div className="row gap-2"><Building2 size={18} /><span className="h3">Organizations &amp; plans</span></div>
          {!orgs ? (
            <Spinner label="Loading organizations…" />
          ) : orgs.length === 0 ? (
            <EmptyState icon={<Building2 size={32} />} title="No organizations yet" />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Organization</th><th>Segment</th><th>Plan</th><th>Max active permits</th><th>Max plants</th><th></th></tr></thead>
                <tbody>
                  {orgs.map((org) => (
                    <OrgRow key={org.id} org={org} busy={busy === `plan-${org.id}`} onSave={(input) => updatePlan(org.id, input)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section className="stack gap-3">
        <div className="row gap-2"><Users size={18} /><span className="h3">Users</span></div>
        {!users ? (
          <Spinner label="Loading users…" />
        ) : users.length === 0 ? (
          <EmptyState icon={<Users size={32} />} title="No users yet" />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Name</th><th>Email</th>{isPlatformAdmin && <th>Organization</th>}<th>Role</th><th>Status</th><th>Last login</th><th></th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td data-label="Name">{u.name ?? '—'}</td>
                    <td data-label="Email" className="body-small text-tertiary">{u.email ?? '—'}</td>
                    {isPlatformAdmin && <td data-label="Organization">{u.orgName ?? '—'}</td>}
                    <td data-label="Role">
                      <select
                        value={u.role}
                        disabled={busy === `role-${u.id}` || (u.id === me?.id)}
                        onChange={(e) => changeRole(u.id, e.target.value as UserRole)}
                      >
                        {ROLES.map((r) => <option key={r} value={r} disabled={r === 'platform_admin' && !isPlatformAdmin}>{titleCase(r)}</option>)}
                      </select>
                    </td>
                    <td data-label="Status"><span className={`status-badge ${u.status === 'active' ? 'clear' : 'critical'}`}>{titleCase(u.status)}</span></td>
                    <td data-label="Last login">{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : 'Never'}</td>
                    <td data-label="">
                      {u.id !== me?.id && (
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={busy === `status-${u.id}`}
                          onClick={() => toggleStatus(u.id, u.status === 'active' ? 'suspended' : 'active')}
                        >
                          {u.status === 'active' ? <><ShieldAlert size={14} /> Suspend</> : 'Reactivate'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function OrgRow({ org, busy, onSave }: { org: Organization; busy: boolean; onSave: (input: { planTier: string; maxActivePermits: number; maxPlants: number }) => void }) {
  const [planTier, setPlanTier] = useState(org.planTier);
  const [maxActivePermits, setMaxActivePermits] = useState(org.maxActivePermits);
  const [maxPlants, setMaxPlants] = useState(org.maxPlants);
  const dirty = planTier !== org.planTier || maxActivePermits !== org.maxActivePermits || maxPlants !== org.maxPlants;

  return (
    <tr>
      <td data-label="Organization" style={{ fontWeight: 600 }}>{org.name}</td>
      <td data-label="Segment" className="body-small text-tertiary">{org.industrySegment}</td>
      <td data-label="Plan">
        <select value={planTier} onChange={(e) => setPlanTier(e.target.value as Organization['planTier'])}>
          {PLAN_TIERS.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
        </select>
      </td>
      <td data-label="Max active permits"><input type="number" style={{ width: 90 }} value={maxActivePermits} onChange={(e) => setMaxActivePermits(Number(e.target.value))} /></td>
      <td data-label="Max plants"><input type="number" style={{ width: 80 }} value={maxPlants} onChange={(e) => setMaxPlants(Number(e.target.value))} /></td>
      <td data-label="">
        {dirty && (
          <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => onSave({ planTier, maxActivePermits, maxPlants })}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        )}
      </td>
    </tr>
  );
}
